import { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import { useHBoxCall } from './hooks/useHBoxCall';
import { useCallDuration } from './hooks/useCallDuration';
import { CallButton } from './CallButton';

import { CLINIC_INFO } from './constants';
import { CallHandlerProps, PatientInfo } from './types';
import { useAuth } from '@/contexts/AuthContext';

const CallHandler: React.FC<CallHandlerProps> = ({ 
  phoneNumber,
  patientName, 
  disabled = false,
  patientId,
  alternatePhone,
  emergencyPhone,
  emergencyContactName,
  hccId,
  clinicName,
  timezone,
  clinicNumbers,
  clinicNumbersSources,
  mobilePhone,
  homePhone,
  defaultSelectedNumber,
  phoneType,
  callButtonVariant = 'default'
}) => {
  const [isCallActive, setIsCallActive] = useState(false);
  const { toast } = useToast();
  const { hboxCall, canTransfer } = useHBoxCall();
  const { callDuration, resetDuration } = useCallDuration(isCallActive);

  const {user} = useAuth();

  // Patient information
  const patientInfo: PatientInfo = {
    patientId: patientId,
    patientName: patientName,
    primaryPhone: `${phoneNumber}`,
    alternatePhone: alternatePhone,
    emergencyPhone: emergencyPhone,
    emergencyContactName: emergencyContactName
  };

  const showPatientCallModal = () => {
    // Check if we have valid numbers available
    const hasMobile = mobilePhone && mobilePhone !== 'n/a';
    const hasHome = homePhone && homePhone !== 'n/a';
    const hasPhone = phoneNumber && phoneNumber !== 'n/a';

    // If no valid numbers available
    if (!hasMobile && !hasHome && !hasPhone) {
      toast({
        title: "Invalid Phone Number",
        description: "Cannot initiate call with invalid phone number",
        variant: "destructive"
      });
      return;
    }

    // Proceed directly with HBox call modal
    initiateCall();
  };

  const initiateCall = () => {
    // Transform clinic numbers - ensure proper structure with all required fields
    const transformedNumbers = clinicNumbers.map(number => ({
      number: number.phone_number,
      number_source_id: number.number_source_id
    }));

    // Transform clinic sources
    const transformedSources = clinicNumbersSources.map(source => ({
      id: source.number_source_id,
      name: source.number_source
    }));

    const modalOptions = {
      // Patient information
      patientId: patientInfo.patientId,
      patientName: patientInfo.patientName,
      primaryPhone: phoneNumber || mobilePhone || homePhone || '',
      alternatePhone: patientInfo.alternatePhone,
      ...(patientInfo.emergencyPhone && { emergencyPhone: patientInfo.emergencyPhone }),
      emergencyContactName: (patientInfo.emergencyPhone && patientInfo.emergencyPhone !== 'n/a' && patientInfo.emergencyContactName) 
        ? patientInfo.emergencyContactName 
        : 'Not available',
      
      // Clinic information - use provided values or fallback to constants
      callerId: user?.id ? Number(user?.id) : null,
      hccId: hccId,
      clinicName: clinicName,
      timezone: timezone,
      clinicNumbers: transformedNumbers,
      clinicNumbersSources: transformedSources,
      
      // Callbacks
      onCallInitiated: (params) => {
        setIsCallActive(true);
        resetDuration();
      },
      onCancel: () => {
        // Handle cancel event
      }
    };
    hboxCall.showNumberSelectionModal(modalOptions);
  };


  // Check if any valid phone numbers are available
  const hasMobile = mobilePhone && mobilePhone !== 'n/a';
  const hasHome = homePhone && homePhone !== 'n/a';
  const hasPhone = phoneNumber && phoneNumber !== 'n/a';
  const hasAnyValidNumber = hasMobile || hasHome || hasPhone;
  
  const isButtonDisabled = disabled || !hasAnyValidNumber || !hboxCall || !clinicNumbers || clinicNumbers.length === 0;

  return (
    <CallButton 
      onClick={showPatientCallModal}
      disabled={isButtonDisabled}
      variant={callButtonVariant}
    />
  );
};

export default CallHandler;